import { useMemo } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { WorkspacePulseEvent } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UseClarificationModuleParams {
  pulseEvents: WorkspacePulseEvent[]
  timeRange: AnalysisTimeRangeKey
  loading?: boolean
  error?: Error | null
  now?: Date
}

export interface UseClarificationModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const isClarificationEvent = (event: WorkspacePulseEvent): boolean => {
  const type = String(event.type || '')
  if (type === 'CLARIFICATION_REQUEST' || /CLARIF/i.test(type)) return true
  const text = String(event.title || '') + ' ' + String(event.summary || '')
  return /clarification|missing context|ambiguity|input needed/i.test(text)
}

export const useClarificationModule = ({
  pulseEvents,
  timeRange,
  loading = false,
  error = null,
  now = new Date(),
}: UseClarificationModuleParams): UseClarificationModuleResult => {
  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    const inWindow = pulseEvents.filter((e) => {
      const d = parseDate(e.createdAt)
      return d && d >= from && d <= to && isClarificationEvent(e)
    })

    const totalCount = inWindow.length

    const dates = inWindow
      .map((e) => parseDate(e.createdAt))
      .filter((d): d is Date => d !== null)

    const points = bucketize(dates, timeRange, now)
    const delta = calculateDelta(points)

    const topRecords: LiveModuleRecord[] = [...inWindow]
      .sort((a, b) => {
        const da = parseDate(a.createdAt)?.getTime() ?? 0
        const db = parseDate(b.createdAt)?.getTime() ?? 0
        return db - da
      })
      .slice(0, 5)
      .map((e, index) => {
        const title = e.title?.trim() || e.summary?.trim() || 'Clarification request'
        const target = e.targetType || 'PLAN'
        const severity = e.severity || 'INFO'
        return {
          id: e.id || `clarification-${index}`,
          primary: title,
          secondary: `${target} · ${severity}`,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = totalCount === 0
      ? { value: '—', label: 'no records' }
      : { value: totalCount, delta, label: 'clarifications' }

    return {
      headline,
      sparkline: { points, direction: 'up-is-bad' },
      records: topRecords,
      loading,
      error,
    }
  }, [pulseEvents, timeRange, loading, error, now])
}
